import { Controller } from "@hotwired/stimulus";

// Primer anchors the panel this far below its trigger.
const ANCHOR_OFFSET = 4;
// Kept clear between the panel and the bottom of the viewport.
const VIEWPORT_MARGIN = 16;

/**
 * GR-3811: open a SelectPanel below its trigger whenever there is reasonable
 * room there.
 *
 * Primer positions the panel with getAnchoredPosition, which flips it above the
 * trigger when the whole panel does not fit underneath. On wizard step 1 the
 * requesting-unit trigger sits near the foot of the page, so the panel opened
 * upward over the fields the requester had just filled in.
 *
 * Just before Primer positions the panel, this caps its height to the room
 * below the trigger, so the panel fits there and Primer keeps it below. When
 * that room is smaller than `minHeight` a panel squeezed into it would show
 * too few choices to be worth it, so the cap is cleared and Primer flips the
 * panel above as before.
 *
 * Attach to the `<select-panel>` element. Primer opens the panel from a click
 * listener on that same element in the bubble phase; this listens in the
 * capture phase, so it always runs first. Enter and Space on the trigger
 * arrive as clicks too.
 *
 * Only a click on the trigger refits. The listener also sees every click
 * inside the open panel, and resizing the dialog there, before Primer measures
 * it for its outside-click test, can close the panel on a click that landed
 * inside it.
 */
export default class extends Controller {
  static values = { minHeight: { type: Number, default: 240 } };

  connect() {
    this.fit = this.fit.bind(this);
    this.element.addEventListener("click", this.fit, true);
  }

  disconnect() {
    this.element.removeEventListener("click", this.fit, true);
  }

  fit(event) {
    const trigger = this.element.ownerDocument.getElementById(`${this.element.id}-button`);
    const dialog = this.element.querySelector("dialog");
    if (!trigger || !dialog) return;
    if (event && !trigger.contains(event.target)) return;

    const below = window.innerHeight - trigger.getBoundingClientRect().bottom - ANCHOR_OFFSET - VIEWPORT_MARGIN;

    if (below >= this.minHeightValue) {
      // Never taller than Primer's own cap for the overlay size.
      dialog.style.maxHeight = `min(${Math.floor(below)}px, calc(100vh - 2rem), var(--overlay-height))`;
    } else {
      dialog.style.maxHeight = "";
    }
  }
}
